import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EnvelopeSimple, CircleNotch, PaperPlaneTilt, Eye } from "@phosphor-icons/react";

export function WeeklyDigestCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const openPreview = async () => {
    setPreviewOpen(true);
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/digest/preview");
      setPreview(data);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    api.get("/digest/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs({ opt_in: false, resend_configured: false }));
  }, []);

  const toggle = async (value) => {
    setSaving(true);
    try {
      const { data } = await api.post("/digest/preferences", { opt_in: value });
      setPrefs((p) => ({ ...p, opt_in: data.opt_in }));
      toast.success(data.rowan_message);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(data.rowan_message);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bk-card p-6" data-testid="weekly-digest-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <EnvelopeSimple size={22} weight="duotone" color="var(--bk-accent)" className="mt-0.5 shrink-0" />
          <div>
            <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Weekly Rowan digest</h4>
            <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>
              A short weekly email across all your businesses: net movement, new subscriptions, price hikes, and team activity.
            </p>
            {prefs?.last_digest_at && (
              <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="digest-last-sent">
                Last sent {new Date(prefs.last_digest_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={openPreview}
            data-testid="digest-preview-button"
            className="px-4 py-2 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors hover:bg-black/5"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          >
            <Eye size={13} weight="bold" /> Preview
          </button>
          <button
            type="button"
            onClick={sendNow}
            disabled={sending || !prefs}
            data-testid="digest-send-now-button"
            className="px-4 py-2 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors hover:bg-black/5 disabled:opacity-60"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          >
            {sending ? <CircleNotch size={13} className="animate-spin" /> : <PaperPlaneTilt size={13} weight="fill" />} Send me one now
          </button>
          <div className="flex items-center gap-2">
            <Switch
              checked={!!prefs?.opt_in}
              disabled={saving || !prefs}
              onCheckedChange={toggle}
              data-testid="digest-opt-in-switch"
            />
            <span className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }} data-testid="digest-opt-in-label">
              {prefs?.opt_in ? "On" : "Off"}
            </span>
          </div>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--bk-text)" }} data-testid="digest-preview-title">
              {preview?.subject || "Your IronRoot week — Rowan's recap"}
            </DialogTitle>
          </DialogHeader>
          {loadingPreview ? (
            <div className="py-12 text-center text-sm inline-flex items-center justify-center gap-2" style={{ color: "var(--bk-text-2)" }} data-testid="digest-preview-loading">
              <CircleNotch size={16} className="animate-spin" /> Building your digest…
            </div>
          ) : preview ? (
            <iframe
              title="Weekly digest preview"
              srcDoc={preview.html}
              sandbox=""
              className="w-full rounded-xl border"
              style={{ height: "65vh", borderColor: "var(--bk-border)", background: "#F8FAFC" }}
              data-testid="digest-preview-frame"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
